import { computed, effect, Injectable, Injector, Signal, signal, inject } from '@angular/core';
import { localStorageFiscalYearIdKey, localStorageOrganizationIdKey } from '../utils/local-storage.utils';
import { ServiceGridItemObject } from '@it2go/types';
import {
    OrganizationSettingsObject,
} from '@libs/types/src/organization/object-type/organization/organization-settings.object';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { selectedOrganizationId } from './current-user-id';
import { OrganizationGlobalStoreType } from './types/organization.global-store.type';
import { LOGGED_USER_GLOBAL_STORE_TOKEN, LoggedUserGlobalStoreType } from './types/logged-user.global-store.type';
import { allServices, userServices } from './services';
import {
    COMMON_SERVICE_SETTINGS_DEFAULT,
    CommonServiceSettingsType,
} from '@libs/types/src/services/types/common-service-settings.type';
import { ServiceSettingsObject } from '@libs/types/src/services/object-type/service/service-settings.object';

export const selectedFiscalYearId = signal<number>(
    Number(localStorage.getItem(localStorageFiscalYearIdKey) || 0),
);

@Injectable({
    providedIn: 'root',
})
export class OrganizationGlobalStore implements OrganizationGlobalStoreType {
    private injector = inject(Injector);


    public selectedId = selectedOrganizationId;
    public selectedFiscalYearId = selectedFiscalYearId;

    public settings = signal<OrganizationSettingsObject[]>([]);

    public services = userServices;
    public servicesInContext = computed(() => {
        return this.services().filter(x => x.type === this.getLoggedUserStore().selectedServiceTypeContext());
    });
    public allServices = allServices;
    // public organizations = signal<OrganizationGridItemObject[]>([]);

    public serviceInContextIds = computed(() => {
        return this.servicesInContext().map((it: ServiceGridItemObject) => it.id);
    });

    public serviceIds = computed(() => {
        return this.services().map((it: ServiceGridItemObject) => it.id);
    });

    private loggedUserStore?: LoggedUserGlobalStoreType;

    constructor() {
        effect(() => {
            localStorage.setItem(localStorageOrganizationIdKey, String(this.selectedId()));
        });
        effect(() => {
            localStorage.setItem(localStorageFiscalYearIdKey, String(this.selectedFiscalYearId()));
        });
    }

    public settingsLoaded() {
        return computed(() => this.settings()?.length);
    }

    public getSettingByKey(key: OrganizationSettingTypeEnum) {
        return computed(() => {
            return this.settings().find((it: OrganizationSettingsObject) => it.key === key) || null;
        });
    }

    public isSettingActive(key: OrganizationSettingTypeEnum) {
        return computed(() => {
            const setting = this.settings().find((it: OrganizationSettingsObject) => it.key === key) || null;
            return setting && setting.active;
        });
    }

    public getServiceById(_id: number | string) {
        const id = Number(_id);
        return computed(() => {
            return this.services().find((it: ServiceGridItemObject) => it.id == id) || null;
        });
    }

    public getServiceBySignalId(id: Signal<number | any>) {
        return computed(() => {
            return this.services().find((it: ServiceGridItemObject) => it.id == id());
        });
    }

    public isServiceSettingsAvailable(
        key: string | null = null,
        serviceId: number | null = null,
    ) {
        if (!key) return true;

        if (serviceId) {
            return this.checkUserServiceAvailableCard(key, serviceId);
        }

        return this.serviceIds().some((it: number) => {
            return this.checkUserServiceAvailableCard(key, it);
        });
    }

    public getServiceSetting<T = CommonServiceSettingsType>(serviceId: number | string, key: string, defaultValue: any = COMMON_SERVICE_SETTINGS_DEFAULT): T{
        const service = this.getServiceById(serviceId)()
        return JSON.parse(service?.serviceSettings.find((it) =>
            it.key === key)?.data || JSON.stringify(defaultValue));
    }

    private getServiceSettings(settings: ServiceSettingsObject[], key: string, defaultValue: any = COMMON_SERVICE_SETTINGS_DEFAULT): CommonServiceSettingsType {
        return JSON.parse(settings.find((it) =>
            it.key === key)?.data || JSON.stringify(defaultValue));
    }

    private checkUserServiceAvailableCard(key: string, serviceId: number) {
        const currentService = this.getServiceById(serviceId)();
        const currentServiceSettings = currentService?.serviceSettings || [];
        const keySettings = this.getServiceSettings(currentServiceSettings, key);

        return keySettings.useCard ?? true;
    }

    private getLoggedUserStore(): LoggedUserGlobalStoreType {
        if (!this.loggedUserStore) {
            this.loggedUserStore = this.injector.get(LOGGED_USER_GLOBAL_STORE_TOKEN);
        }
        return this.loggedUserStore;
    }

}
